import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentResult({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking");
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");

  useEffect(() => {
    if (mode === "cancel") return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderNumber(data.order_number || "");
        if (data.payment_status === "paid") {
          setState("paid");
          // Order is only confirmed & kept once paid
          try { if (data.order_number) localStorage.setItem("metros_last_order", data.order_number); } catch (e) { console.warn("Could not persist last order number:", e); }
          clearCart();
          setTimeout(() => navigate(`/order/${data.order_number}`), 1200);
          return;
        }
        if (data.payment_status === "failed") { setState("failed"); return; }
      } catch (e) { console.warn("Payment status poll failed, retrying:", e); }
      attempts += 1;
      if (attempts >= 8) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, params, navigate]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="payment-result">
      {state === "checking" && (<><Loader2 className="mx-auto animate-spin text-metros-red" size={40} /><p className="font-heading font-bold text-xl mt-4">Confirming your payment…</p><p className="text-sm text-muted-foreground">Please wait, don't close this page.</p></>)}
      {state === "paid" && (<><CheckCircle2 className="mx-auto text-green-500" size={48} /><p className="font-heading font-extrabold text-2xl mt-4" data-testid="payment-paid">Payment successful!</p><p className="text-sm text-muted-foreground">Taking you to your order…</p></>)}
      {(state === "cancelled" || state === "failed" || state === "pending" || state === "error") && (
        <>
          <XCircle className="mx-auto text-metros-red" size={48} />
          <p className="font-heading font-extrabold text-2xl mt-4">
            {state === "cancelled" ? "Payment cancelled" : state === "pending" ? "Payment still processing" : "Payment not completed"}
          </p>
          <p className="text-sm text-muted-foreground mt-1" data-testid="payment-not-placed">
            {state === "pending"
              ? "If your payment completes, your order will be confirmed and you'll get an email. Otherwise nothing was charged."
              : "No payment was taken, so your order was not placed. Your basket is still saved — you can try again."}
          </p>
          <div className="flex gap-3 mt-6 justify-center">
            <Link to="/checkout" className="bg-metros-black text-white rounded-full px-6 py-3 font-semibold" data-testid="try-again">Try again</Link>
            <Link to="/menu" className="bg-metros-red text-white rounded-full px-6 py-3 font-semibold">Back to Menu</Link>
          </div>
        </>
      )}
    </div>
  );
}
